import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Avatar } from '@mui/material';
import { Note as NoteIcon } from '@mui/icons-material';
import NoteCard from '../../NoteCard';

const TimelineView = ({ notes, onNoteSelect }) => {
  const handleNoteClick = (note) => {
    if (onNoteSelect) {
      onNoteSelect(note);
    }
  };

  const sortedNotes = useMemo(() => {
    return [...notes].sort((a, b) => {
      const dateA = a.createdAt || 0;
      const dateB = b.createdAt || 0;
      return dateB - dateA; // Most recent first
    });
  }, [notes]);

  const groupNotesByDate = (notesList) => {
    const groups = {};
    notesList.forEach((note) => {
      const date = new Date(note.createdAt || Date.now());
      const dateKey = date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(note);
    });
    return groups;
  };

  const groupedNotes = useMemo(() => groupNotesByDate(sortedNotes), [sortedNotes]);

  if (notes.length === 0) {
    return (
      <Box
        sx={{
          flexGrow: 1,
          height: '100%',
          overflow: 'auto',
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          color: 'text.secondary',
          gap: 2,
        }}
      >
        <Typography variant="h6">No notes yet</Typography>
        <Typography variant="body2">Create a note to start organizing your ideas.</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flexGrow: 1,
        height: '100%',
        overflow: 'auto',
        p: 3,
      }}
    >
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        Timeline View
      </Typography>

      <Box sx={{ position: 'relative', pl: 4 }}>
        {Object.entries(groupedNotes).map(([dateKey, dateNotes]) => (
          <React.Fragment key={dateKey}>
            {dateNotes.map((note, noteIndex) => (
              <Box
                key={note._id || note.id || note.noteId}
                sx={{
                  position: 'relative',
                  mb: 3,
                  '&:not(:last-child)::before': {
                    content: '""',
                    position: 'absolute',
                    left: '-20px',
                    top: '40px',
                    width: '2px',
                    height: 'calc(100% + 16px)',
                    backgroundColor: 'divider',
                  },
                }}
              >
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box
                    sx={{
                      position: 'absolute',
                      left: '-28px',
                      top: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 40,
                        height: 40,
                        bgcolor: 'primary.main',
                        border: '2px solid',
                        borderColor: 'background.paper',
                      }}
                    >
                      <NoteIcon fontSize="small" />
                    </Avatar>
                  </Box>
                  <Box sx={{ flexGrow: 1 }}>
                    {noteIndex === 0 && (
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                        {dateKey}
                      </Typography>
                    )}
                    <Box
                      sx={{
                        cursor: 'pointer',
                        '&:hover .MuiCard-root': {
                          boxShadow: 4,
                          transform: 'translateY(-2px)',
                        },
                      }}
                    >
                      <NoteCard note={note} onClick={handleNoteClick} />
                    </Box>
                  </Box>
                </Box>
              </Box>
            ))}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
};

TimelineView.propTypes = {
  notes: PropTypes.arrayOf(PropTypes.object).isRequired,
  onNoteSelect: PropTypes.func,
};

TimelineView.defaultProps = {
  onNoteSelect: null,
};

export default TimelineView;
